import { useState, useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import talentLogo from "../assets/talentlogo.png";
import talentLogo2 from "../assets/talentlogo2.png";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import {
  addSelectedLocationFromCheckinUrl,
  extractManageContext,
  loadManageContext,
  saveManageContext,
} from "./manageContext";
import { hasKioskTimesheetSearchParams, resolveKioskSearchFromStorage } from "./deviceKiosk";
import { loadManageSession } from "./manageSession";
import { formatAppIntlLabel, getAppDisplayTimezone } from "../utils/appDateTime";

export default function CheckInHome() {
  usePageMeta("Check In");
  const navigate = useNavigate();
  const location = useLocation();
  const { checkinComplete, mode, account } = location.state || {};
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userName, setUserName] = useState("User"); // TODO: Get actual user name from localStorage/API
  const [showSuccess, setShowSuccess] = useState(!!checkinComplete);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const hasKioskContext = hasKioskTimesheetSearchParams(location.search);

  // Clear stale kiosk location assignments if we are loading a timesheet check-in link on a non-kiosk device
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasClientId = urlParams.get('clientId');
    if (hasClientId) {
      try {
        const stored = localStorage.getItem('manageContext');
        if (stored) {
          const parsed = JSON.parse(stored);
          // If this is NOT a registered kiosk device (no clientDeviceId),
          // clear the context to avoid cross-client location pollution.
          if (!parsed?.clientDeviceId) {
            localStorage.removeItem('manageContext');
          }
        }
      } catch (e) {
        console.warn('Error clearing stale manageContext:', e);
      }
    }
  }, []);

  // Check-in link carrying a locationId (timesheet QR): add it to the stored selected locations
  useEffect(() => {
    try {
      addSelectedLocationFromCheckinUrl(location.search);
    } catch (e) {
      console.warn('Error updating manageSelectedLocations from URL:', e);
    }
  }, [location.search]);

  // Load QR data from localStorage or fetch from URL if qrToken is present
  // Also check URL parameters for client and location info
  useEffect(() => {
    const loadQrData = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      
      // First, check localStorage for QR data
      try {
        const storedQrData = localStorage.getItem('lastQrData');
        if (storedQrData) {
          const parsed = JSON.parse(storedQrData);
          const urlClientId = urlParams.get('clientId');
          // Verify it has the required data and matches the current client in the URL
          if (parsed.clientDetails && parsed.clientDetails.clientName) {
            const storedClientId = parsed.clientDetails.clientId || parsed.clientDetails.client_id;
            if (!urlClientId || String(storedClientId) === String(urlClientId)) {
              setQrData(parsed);
              return;
            } else {
              localStorage.removeItem('lastQrData');
            }
          }
        }
      } catch (e) {
        console.warn('Error loading QR data from localStorage:', e);
      }

      // Check URL for qrToken
      const qrToken = urlParams.get('qrToken');
      if (qrToken) {
        setQrLoading(true);
        try {
          const response = await fetch(`${BASE_URL}/api/shifts/qr-checkin`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token: qrToken })
          });

          if (response.ok) {
            const result = await response.json();
            if (result.clientDetails && result.clientDetails.clientName) {
              setQrData(result);
              localStorage.setItem('lastQrData', JSON.stringify(result));
            }
          }
        } catch (e) {
          console.warn('Error fetching QR data:', e);
        } finally {
          setQrLoading(false);
        }
      } else {
        // No QR token - check URL parameters for client and location
        const clientParam = urlParams.get('client');
        const clientIdParam = urlParams.get('clientId');
        const locationParam = urlParams.get('location');
        const fromParam = urlParams.get('from'); // Date parameter
        const toParam = urlParams.get('to');
        const shiftDateParam = urlParams.get('shiftDate');
        
        if (clientParam) {
          setClientInfo({
            clientId: clientIdParam || null,
            clientName: decodeURIComponent(clientParam),
            location: locationParam ? decodeURIComponent(locationParam) : null,
            shiftDate: shiftDateParam || fromParam || null,
            startDate: fromParam || shiftDateParam || null,
            endDate: toParam || fromParam || shiftDateParam || null,
          });
          
          // If location is not in URL but we have client and date, try to fetch location from backend
          if (!locationParam && fromParam) {
            // TODO: Fetch location from backend based on client name and date
            // For now, location will be null and won't be displayed
          }
        }
      }
    };

    loadQrData();
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) =>
    formatAppIntlLabel(date, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: getAppDisplayTimezone(),
    });

  const handleManageClick = () => {
    const extractedContext = extractManageContext({ qrData, clientInfo, userName });
    const existingContext = loadManageContext(location);
    const manageContext = {
      ...existingContext,
      ...extractedContext,
      selectedLocationIds:
        extractedContext?.selectedLocationIds?.length > 0
          ? extractedContext.selectedLocationIds
          : existingContext?.selectedLocationIds || [],
    };
    saveManageContext(manageContext);

    const params = new URLSearchParams({
      name: manageContext.clientName,
      email: manageContext.email,
    });
    if (manageContext.shiftId) params.set("shiftId", String(manageContext.shiftId));
    if (manageContext.timesheetId) params.set("timesheetId", String(manageContext.timesheetId));
    if (manageContext.clientId) params.set("clientId", String(manageContext.clientId));
    if (manageContext.locationName) params.set("location", manageContext.locationName);
    if (manageContext.shiftDate) params.set("shiftDate", manageContext.shiftDate);
    if (manageContext.startDate) params.set("from", manageContext.startDate);
    if (manageContext.endDate) params.set("to", manageContext.endDate);
    if (manageContext.accountId) params.set("accountId", String(manageContext.accountId));
    if (manageContext.clientDeviceId) {
      params.set("clientDeviceId", String(manageContext.clientDeviceId));
    }
    if (Array.isArray(manageContext.selectedLocationIds) && manageContext.selectedLocationIds.length) {
      params.set("selectedLocationIds", manageContext.selectedLocationIds.join(","));
    }

    const session = loadManageSession();
    if (session?.token && session?.user) {
      navigate(`/manage/dashboard?${params.toString()}`, {
        state: { manageContext },
      });
      return;
    }

    navigate(`/manage/signin?${params.toString()}`, { state: { manageContext } });
  };

  if (!hasKioskContext) {
    const session = loadManageSession();
    const restored = resolveKioskSearchFromStorage();
    if (session?.token && restored) {
      return <Navigate to={`/checkin?${restored.toString()}`} replace />;
    }
    return <Navigate to="/device" replace />;
  }

  return (
    <div className="h-screen bg-gray-50 relative overflow-hidden flex flex-col">
      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mx-4 mt-4 relative" role="alert">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <strong className="font-bold">Success!</strong>
              <span className="block sm:inline ml-1">
                {mode === 'checkin' ? 'Checked in' : 'Checked out'} successfully
                {account?.name && ` for ${account.name}`}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowSuccess(false)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Top Bar */}
      <div className="flex justify-between items-start p-4">
        {/* Top Left Logo */}
        <div className="flex items-center justify-center cursor-pointer" onClick={() => navigate(`/checkin${location.search}`)}>
          <img src={talentLogo2} alt="Talent Logo" className="h-12 w-auto" />
        </div>
        
        {/* Top Right - Time and Location/Client Info */}
        <div className="text-right">
          <div className="text-xl font-bold text-gray-800">{formatTime(currentTime)}</div>
          {qrData?.clientDetails ? (
            <>
              <div className="text-sm font-semibold text-gray-800">{qrData.clientDetails.clientName || "Unknown Client"}</div>
              {(qrData.shiftDetails?.location || qrData.timesheetDetails?.location) && (
                <div className="text-xs font-medium text-gray-700">
                  {qrData.shiftDetails?.location || qrData.timesheetDetails?.location}
                </div>
              )}
              {qrData.clientDetails.clientAddress && (
                <div className="text-xs text-gray-600">{qrData.clientDetails.clientAddress}</div>
              )}
            </>
          ) : clientInfo ? (
            <>
              <div className="text-sm font-semibold text-gray-800">{clientInfo.clientName}</div>
              {clientInfo.location && (
                <div className="text-xs font-medium text-gray-700">{clientInfo.location}</div>
              )}
            </>
          ) : (
            <div className="text-sm text-gray-600">{userName}</div>
          )}
        </div>
      </div>

      {/* Center Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-4 lg:pb-0">
        {/* Large Logo */}
        <div className="mb-8">
          <img src={talentLogo} alt="Talent Employment Inc." className="h-48 w-auto" />
        </div>

        {/* Buttons */}
        <div className="space-y-12 lg:space-y-3 w-full max-w-md mt-48 lg:mt-0">
          <button
            onClick={() => navigate(`/checkin/scan${location.search}`)}
            className="w-full bg-[#DC1F00] text-white py-3 px-6 rounded-lg font-semibold text-base hover:bg-[#b91900] transition-colors"
          >
            CHECK IN OR CHECK OUT
          </button>

          <button
            onClick={handleManageClick}
            className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold text-base hover:bg-gray-700 transition-colors"
          >
            MANAGE
          </button>
        </div>
      </div>
    </div>
  );
}
